import {gauge} from 'metrics/Metrics';
import AIFillStructuredTableModal from 'notebook/ai-tools/flexible-data-entry/AIFillStructuredTableModal';
import {makeDefaultItem} from 'notebook/notes/CreateOps';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import NoteConstants from 'notebook/notes/NoteConstants';
import * as NoteStoreTransforms from 'notebook/notes/NoteStoreTransforms';
import {StructuredTable, StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {Row} from 'notebook/tables/constants/RowConstants';
import {
  FromServerNoteLinkedFile,
  NoteLinkedFile,
  NotePart,
  NotePartWithItem,
} from 'notebook/util/NoteUtilConstants';
import EntityStore from 'stores/EntityStore';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as DateUtil from 'util/DateUtil';
import {withLoadingScreen} from 'util/Promises';

export const SUPPORTED_TABLE_KINDS_FOR_AI_DATA_ENTRY = [
  StructuredTableKind.REGISTRATION,
  StructuredTableKind.RESULTS,
  StructuredTableKind.TRANSFER,
  StructuredTableKind.BOX_CREATION,
  StructuredTableKind.PLATE_CREATION,
  StructuredTableKind.INVENTORY,
];

export interface AddStructuredTableRowsUsingAIResult {
  /**
   * New rows to add to the table. We ensure that these are valid, e.g. do not edit read-only columns.
   */
  newRows: {[tableName: string]: Array<Row>};
  /**
   * Note parts to append after the table. This is used to attach user-uploaded files if necessary.
   */
  noteParts?: Array<NotePart>;
  cidIdMap?: CidIdMap;
}

/**
 * Creates a File object from prompt text for upload
 * Returns a File object that can be uploaded using startFileUpload
 */
export function convertPromptToFile(promptText: string): File {
  const currentUser = EntityStore.currentUser();
  const dateOptions = {showYear: true, showTime: true};
  const dateString = DateUtil.readableTime(Date.now(), dateOptions);

  const formattedPromptText = promptText.startsWith('Example row:') ? `\n${promptText}` : promptText;

  const content = `\
User:
${currentUser.name}
${currentUser.email}

Time submitted:
${dateString}

User prompt:
${formattedPromptText}`;

  return new File([content], `Data Entry record.txt`, {
    type: 'text/plain',
  });
}

/**
 * Creates a note linked object part from a file and prepares it for the store
 */
function createNoteLinkedObject(file: FromServerNoteLinkedFile, cidIdMap: CidIdMap): NotePart {
  const notePart: NotePartWithItem<NoteLinkedFile> = {
    ...makeDefaultItem(NoteConstants.NOTE_LINKED_OBJECT),
    item_id: file.id,
    // @ts-expect-error(2322): TODO(BNCH-129574): prepareForStores should convert FromServerNotePart to NoteParts
    item: file,
    alignment: 'left',
    listType: null,
    placeholder: false,
  };
  // @ts-expect-error(2322): TODO(BNCH-129574): prepareForStores should convert FromServerNotePart to NoteParts
  NoteStoreTransforms.prepareNLOForStore(notePart, cidIdMap);
  return notePart;
}

async function gaugePromiseDurationSeconds<T>(name: string, promise: Promise<T>): Promise<T> {
  const startTime = new Date();
  return await promise.finally(() => {
    // getTime is precise enough for our use case (vs. Performance API)
    const duration = new Date().getTime() - startTime.getTime();
    gauge(name, duration / 1000);
  });
}

/**
 * Creates note parts from pre-uploaded files
 */
async function createNotePartsFromUploadedFiles(
  fileNoteLinkedFilesPromise: Promise<Array<FromServerNoteLinkedFile>>,
  promptNoteLinkedFilePromise: Promise<FromServerNoteLinkedFile>,
  tableNames: Array<string>
): Promise<{noteParts: Array<NotePart>; cidIdMap: CidIdMap}> {
  const cidIdMap = {};

  const options = {showYear: true, showTime: true};
  const text = `Data Entry upload for ${tableNames.join(', ')} on ${DateUtil.readableTime(Date.now(), options)}`;
  const textNotePart = makeDefaultItem(NoteConstants.TEXT, text, {shouldGenerateCid: false});
  NoteStoreTransforms.prepareNotePartForStore(textNotePart, cidIdMap);

  const uploadedFiles = await gaugePromiseDurationSeconds(
    // File uploads are slow, but we expect AI to be the bottleneck; by doing the two in parallel, we hope
    // that file uploads introduce zero additional blocking time. Track to see if this is the case.
    'addStructuredTableRowsUsingAI.blockingFileUploadDuration_s',
    // Promise.all will fail with the first rejected promise. We intentionally do not catch it, to rely
    // on our global promise rejection handler to show a toast.
    Promise.all([promptNoteLinkedFilePromise, fileNoteLinkedFilesPromise]).then(
      ([promptNoteLinkedFile, fileNoteLinkedFiles]) => [promptNoteLinkedFile, ...fileNoteLinkedFiles]
    )
  );
  const fileNoteParts = uploadedFiles.map((file) => createNoteLinkedObject(file, cidIdMap));

  const noteParts = [textNotePart, ...fileNoteParts];

  return {
    // @ts-expect-error(2322): TODO(BNCH-129574): prepareForStores should convert FromServerNotePart to NoteParts
    noteParts,
    cidIdMap,
  };
}

/**
 * For a given structured table, prompt the user to add new rows, with details inferred by AI.
 *
 * Currently, we only allow adding rows, though in the future we may want to support edits and
 * other operations.
 *
 * Note that this currently should only be used for structured tables where column metadata is
 * enough to know if a cell is fillable by the user. See ai_data_entry_notebook_util.py on the
 * backend for details.
 */
export default async function addStructuredTableRowsUsingAI(
  entryId: string,
  structuredTables: Array<StructuredTable<unknown, unknown>>,
  initialPromptText?: string,
  initialSelectedTableName?: string
): Promise<AddStructuredTableRowsUsingAIResult | null> {
  const modalResult = await legacyRunModal(AIFillStructuredTableModal, {
    entryId,
    structuredTables,
    initialPromptText,
    initialSelectedTableName,
  });
  if (modalResult == null) {
    return null;
  }

  const {noteParts, cidIdMap} = await withLoadingScreen(
    createNotePartsFromUploadedFiles(
      modalResult.fileNoteLinkedFilesPromise,
      modalResult.promptNoteLinkedFilePromise,
      Object.keys(modalResult.newRows)
    ),
    'Attaching files...'
  );

  return {newRows: modalResult.newRows, noteParts, cidIdMap};
}
